﻿// ============================================
// P0-② 场景属性面板（SceneSettingsPanel，2026-10-15）
// 定稿分组（主方案 §4.6 / P0 架构 §六）：基础设置 / 天空 / 光照 / 阴影 / 雾 / 渲染 / 后期
// 原则：雾/环境光/平行光 = 环境与大气光照；「渲染/后期」只放画面输出与成像参数（P1 接线）。
// 入口：场景树「场景」节点（P0-③）/ PropertyTreePanel「环境」节点（EnvironmentPanel 薄壳）。
// 术语约定：「问标」= 表单 label 后的圆圈问号（Form.Item tooltip = 说明 + 使用案例）。
// ============================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Collapse, Form, Switch, Slider, ColorPicker, InputNumber, Input, Select, Segmented, Button, Typography, Space, Tag, Upload, Progress, message } from 'antd';
import { ReloadOutlined, UploadOutlined, DeleteOutlined, EnvironmentOutlined, ThunderboltOutlined, AimOutlined } from '@ant-design/icons';
import * as THREE from 'three';
import useSceneStore, { type SceneAnnotation, type AnnotationKind, type VolumeElement } from '../../stores/sceneStore';
import useEditorUiStore from '../../stores/editorUiStore';
import { useSelectionStore } from '../../stores/selectionStore';
import { useDeviceDataStore } from '../../stores/deviceDataStore';
import { modelRefsRegistry } from '../../stores/modelRefsRegistry';
import { beginGestureHistory, endGestureHistory } from '../../stores/dashboardHistory';
import { uploadEnvMap } from '../../api/models';
import { fetchIotDevices } from '../../api/iot';
import { DEFAULT_SHADOW_SETTINGS } from '../../utils/shadowExtent';
import { WEATHER_PRESETS } from '../../utils/weatherPresets';
import { sunFromTime, formatTime } from '../../utils/sunTime';
import { performanceTierPatch, type PerformanceTier } from '../../utils/performanceTier';
import { ensurePaidFeature, isCommercialAnnotationKind } from '../../utils/entitlement';
import { cloudCountFromDensity } from '../../utils/volumeCloud';
import { annotationTemplatesOf, volumePresetsOf, presetPriceLabel, templateToAnnotationDrafts, templateToVolumeDrafts } from '../../utils/marketPresets';
import usePluginStore from '../../plugins/plugin-manager';
import { reportComponentUsage } from '../../plugins/plugin-api';
import { usePopupStore } from '../../stores/popupStore';
import { isAreaKind, isArrowKind, needsExplicitFinish, kindLabel } from '../../utils/annotations/primitives';
import { groundPointsToSurface } from '../../utils/annotations/grounding';
import { annotationsToGeoJson, geoJsonToAnnotations } from '../../utils/annotations/geo';
import { defaultStyleFor } from './AnnotationLayer';

const { Text } = Typography;

/** 帧耗时 EMA（毫秒）——硬件负载只读徽标（§9.3）。节流到 ~2Hz 更新，避免每帧 setState。 */
function useFrameLoadEma(): number {
  const [ms, setMs] = useState(16.7);
  useEffect(() => {
    let raf = 0;
    let last = performance.now();
    let ema = 16.7;
    let lastFlush = performance.now();
    const tick = (t: number) => {
      const dt = t - last;
      last = t;
      if (dt > 0 && dt < 2000) ema = ema * 0.9 + dt * 0.1;
      if (t - lastFlush > 500) {
        lastFlush = t;
        setMs(ema);
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);
  return ms;
}

/** 问标行：label + tooltip（说明 + 使用案例）；控件自带 testid 供 e2e 定位 */
function Field({ label, tooltip, children }: { label: string; tooltip: string; children: React.ReactNode }) {
  return (
    <Form.Item label={label} tooltip={tooltip} style={{ marginBottom: 12 }}>
      {children}
    </Form.Item>
  );
}

const GROUP_DEFAULTS = {
  basic: { boundedMode: false, showGround: true, showGrid: true, backgroundColor: '#87CEEB', cameraStrategy: 'auto' as const },
  sky: { enabled: false, sunElevation: 0.5, sunAzimuth: 0.8 },
  light: { ambientIntensity: 0.6, ambientColor: '#e8f0ff', directionalIntensity: 1.2, directionalColor: '#fffbe6' },
  fog: {
    fogEnabled: false,
    fog: { density: 0.008, color: '#c8e4f8', startDistance: 0, endDistance: 0, maxOpacity: 1, affectedBySky: false, heightFalloff: 0, heightOffset: 0 },
  },
};

export default function SceneSettingsPanel() {
  const environment = useSceneStore((s) => s.environment);
  const setEnvironment = useSceneStore((s) => s.setEnvironment);
  const effects = useSceneStore((s) => s.effects);
  const setEffects = useSceneStore((s) => s.setEffects);
  const boundedMode = useSceneStore((s) => s.boundedMode);
  const setBoundedMode = useSceneStore((s) => s.setBoundedMode);
  const showGrid = useSceneStore((s) => s.showGrid);
  const setShowGrid = useSceneStore((s) => s.setShowGrid);
  const showGround = useSceneStore((s) => s.showGround);
  const setShowGround = useSceneStore((s) => s.setShowGround);
  const cameraStrategy = useSceneStore((s) => s.cameraStrategy);
  const setCameraStrategy = useSceneStore((s) => s.setCameraStrategy);

  const gridAuto = useEditorUiStore((s) => s.gridAuto);
  const gridCellSize = useEditorUiStore((s) => s.gridCellSize);

  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  // 2026-10-16（P2 性能档）：当前档位（面板内状态；不随场景保存，避免与手动微调冲突）
  const [perfTier, setPerfTier] = useState<PerformanceTier | undefined>(undefined);

  const shadow = environment.shadow ?? DEFAULT_SHADOW_SETTINGS;
  const sky = effects.sky;
  // 2026-10-16（P1-2 雾增强）：nested fog（旧扁平字段由 loadEnvironment 迁移）
  const fog = environment.fog ?? { density: 0.008, color: '#c8e4f8', startDistance: 0, endDistance: 0, maxOpacity: 1, affectedBySky: false, heightFalloff: 0, heightOffset: 0 };
  // 2026-10-16（P1-1）：渲染/后期接线（EffectComposer 消费 effects.postprocessing）
  const postprocessing = effects.postprocessing;
  // 2026-10-17（体积元素 S1）
  const volumes = useSceneStore((s) => s.volumes);
  const addVolume = useSceneStore((s) => s.addVolume);
  const updateVolume = useSceneStore((s) => s.updateVolume);
  const removeVolume = useSceneStore((s) => s.removeVolume);
  const volumeSelectedId = useSelectionStore((s) => s.volumeSelectedId);
  // 2026-10-17（标绘层 S2）
  const annotations = useSceneStore((s) => s.annotations);
  const updateAnnotation = useSceneStore((s) => s.updateAnnotation);
  const removeAnnotation = useSceneStore((s) => s.removeAnnotation);
  const annotationSelectedId = useSelectionStore((s) => s.annotationSelectedId);
  const annotationDrawKind = useEditorUiStore((s) => s.annotationDrawKind);
  const setAnnotationDrawKind = useEditorUiStore((s) => s.setAnnotationDrawKind);
  const selectVolume = useSelectionStore((s) => s.selectVolume);
  // 2026-10-17（标绘层 S3）：落点方式/吸附 + 数据绑定 + GeoJSON
  const addAnnotation = useSceneStore((s) => s.addAnnotation);
  const currentScene = useSceneStore((s) => s.currentScene);
  const annotationPlaneMode = useEditorUiStore((s) => s.annotationPlaneMode);
  const setAnnotationPlaneMode = useEditorUiStore((s) => s.setAnnotationPlaneMode);
  const annotationSnap = useEditorUiStore((s) => s.annotationSnap);
  const setAnnotationSnap = useEditorUiStore((s) => s.setAnnotationSnap);
  const deviceMap = useDeviceDataStore((s) => s.deviceMap);
  const [iotDevices, setIotDevices] = useState<{ value: string; label: string }[]>([]);
  const geojsonInputRef = useRef<HTMLInputElement>(null);
  // 设备注册表：仅在有标绘（需要绑定下拉）时拉取，避免无谓请求/测试噪声
  useEffect(() => {
    if (!annotations.length || iotDevices.length) return;
    fetchIotDevices()
      .then((list) => setIotDevices(list.map((d) => ({ value: d.deviceId, label: `${d.name}${d.imei && d.imei !== d.name ? ` (${d.imei})` : ''}` }))))
      .catch(() => {});
  }, [annotations.length, iotDevices.length]);

  /** S3：进入绘制态（商用标绘=箭头族需 Pro；免费档拦截并弹升级引导） */
  const tryDrawAnnotation = useCallback(async (kind: AnnotationKind) => {
    if (useEditorUiStore.getState().annotationDrawKind === kind) { setAnnotationDrawKind(null); return; }
    if (isCommercialAnnotationKind(kind)) {
      const ok = await ensurePaidFeature('annotation');
      if (!ok) return;
    }
    setAnnotationDrawKind(kind);
  }, [setAnnotationDrawKind]);

  /** M2：新增体积元素需 Pro（免费档拦截并弹升级引导） */
  const tryAddVolume = useCallback(async (v: Omit<VolumeElement, 'id'>) => {
    const ok = await ensurePaidFeature('volume');
    if (ok) addVolume(v);
  }, [addVolume]);

  /** S3：「贴地」— 关键点与采样点逐个落到模型表面（射线向下；未命中保留原 y） */
  const regroundAnnotation = useCallback((a: SceneAnnotation) => {
    const resolveY = (x: number, z: number): number | null => {
      const objects: THREE.Object3D[] = [];
      modelRefsRegistry.getAll().forEach((h) => { if (h?.group) objects.push(h.group); });
      if (!objects.length) return null;
      const rc = new THREE.Raycaster(new THREE.Vector3(x, 1e5, z), new THREE.Vector3(0, -1, 0), 0, 3e5);
      const hits = rc.intersectObjects(objects, true);
      return hits.length ? hits[0].point.y : null;
    };
    const pts = groundPointsToSurface(a.points, resolveY, 2);
    beginGestureHistory();
    updateAnnotation(a.id, { planeMode: 'model', points: pts });
    endGestureHistory();
  }, [updateAnnotation]);

  /** S3：GeoJSON 导出（数据互通；无运行期依赖） */
  const exportGeoJson = useCallback(() => {
    if (!annotations.length) { message.info('暂无标绘可导出'); return; }
    const fc = annotationsToGeoJson(annotations.map((a) => ({
      kind: a.kind, name: a.name, points: a.points, style: a.style as unknown as Record<string, unknown>,
    })));
    const blob = new Blob([JSON.stringify(fc, null, 2)], { type: 'application/geo+json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${currentScene?.name || 'annotations'}.geojson`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [annotations, currentScene?.name]);

  /** S3：GeoJSON 导入（追加到当前场景；支持 FeatureCollection/Feature/geometry） */
  const importGeoJsonFile = useCallback(async (file: File) => {
    try {
      const text = await file.text();
      const items = geoJsonToAnnotations(text);
      let firstId: string | null = null;
      for (const it of items) {
        const id = addAnnotation({
          kind: it.kind,
          name: it.name || `${kindLabel(it.kind)} ${useSceneStore.getState().annotations.length + 1}`,
          points: it.points,
          planeMode: 'ground',
          style: { ...defaultStyleFor(it.kind), ...(it.style || {}) } as SceneAnnotation['style'],
          visible: true,
        });
        if (!firstId) firstId = id;
      }
      if (firstId) useSelectionStore.getState().selectAnnotation(firstId);
      message.success(`已导入 ${items.length} 个标绘（GeoJSON）`);
    } catch (err: any) {
      message.error(`GeoJSON 导入失败：${err?.message || '格式无效'}`);
    }
  }, [addAnnotation]);

  // ---------- M3：市场预设（标绘模板 / 体积预设）应用 ----------
  const marketComponents = usePluginStore((s) => s.marketComponents);
  const annoTemplates = useMemo(() => annotationTemplatesOf(marketComponents), [marketComponents]);
  const volPresets = useMemo(() => volumePresetsOf(marketComponents), [marketComponents]);

  /** 应用市场预设：M2 权益挂点（体积预设/商用标绘模板=Pro；市场付费/积分另计）→ 生成草稿落库 → 用量上报 */
  const applyMarketPreset = useCallback(async (item: (typeof marketComponents)[number], type: 'annotation' | 'volume') => {
    if (item.entitled === false) {
      if (item.priceType === 'credits') {
        message.warning(`积分不足：本预设需 ${Number(item.creditPrice ?? 0)} 积分/次，请充值后再试`);
      } else {
        message.warning('该预设需开通对应套餐后使用');
        usePopupStore.getState().openUpgrade();
      }
      return;
    }
    try {
      if (type === 'annotation') {
        const drafts = templateToAnnotationDrafts(item).filter((d) => d.points.length > 0);
        if (!drafts.length) { message.warning('模板无可落位的标绘（缺少关键点）'); return; }
        // M2：商用标绘模板（含箭头族）挂 Pro；基础标绘模板免费
        if (drafts.some((d) => isCommercialAnnotationKind(d.kind))) {
          const ok = await ensurePaidFeature('annotation');
          if (!ok) return;
        }
        const ids = drafts.map((d) => addAnnotation({
          kind: d.kind as never,
          name: d.name,
          points: d.points,
          planeMode: d.planeMode,
          style: { ...defaultStyleFor(d.kind as never), ...(d.style as object | undefined) } as never,
          visible: true,
          animation: d.animation,
        }));
        if (ids[0]) useSelectionStore.getState().selectAnnotation(ids[0]);
        message.success(`已应用标绘模板「${item.name}」（${drafts.length} 个标绘）`);
      } else {
        // M2：应用体积预设 = 体积元素，挂 Pro（与「+ 区域覆盖/+ 光柱/+ 雾盒/+ 云团」同门禁）
        const ok = await ensurePaidFeature('volume');
        if (!ok) return;
        const drafts = templateToVolumeDrafts(item);
        drafts.forEach((d) => addVolume({
          kind: d.kind as never,
          name: d.name,
          enabled: true,
          shape: d.shape,
          position: d.position,
          size: d.size,
          blendDistance: d.blendDistance,
          priority: d.priority,
          envs: d.envs,
          ray: d.ray as never,
          vol: d.vol as never,
        }));
        message.success(`已应用体积预设「${item.name}」（${drafts.length} 个体积）`);
      }
      // 付费/积分预设：按次上报（服务端权威扣减/计量；失败静默由服务端降级）
      if (item.priceType === 'paid' || item.priceType === 'credits') {
        void reportComponentUsage(item.id).catch(() => {});
      }
    } catch (e) {
      message.error(`应用失败：${(e as Error).message}`);
    }
  }, [addAnnotation, addVolume, marketComponents]);

  const drawHint = (kind: AnnotationKind): string => {
    if (kind === 'point') return '单击落点即完成';
    if (kind === 'text') return '单击落点即完成（内容在卡片中编辑）';
    if (kind === 'freehandLine' || kind === 'freehandPolygon') return '按住左键拖拽绘制，松开完成';
    if (kind === 'polyline' || kind === 'polygon' || kind === 'curve') return '点击落点，双击或「完成」结束';
    if (kind === 'sector' || kind === 'arrowCurve') return '点击 3 点（由面板「落点」决定地面/模型表面）';
    return '点击 2 点（起点 → 终点/对角）';
  };

  const frameMs = useFrameLoadEma();
  const fps = frameMs > 0 ? Math.round(1000 / frameMs) : 0;
  const loadLevel = frameMs <= 22 ? 'good' : frameMs <= 40 ? 'warn' : 'bad';
  const loadColor = loadLevel === 'good' ? '#52c41a' : loadLevel === 'warn' ? '#faad14' : '#ff4d4f';
  const loadText = loadLevel === 'good' ? '流畅' : loadLevel === 'warn' ? '一般' : '吃力';

  const setShadow = useCallback((patch: Partial<typeof shadow>) => {
    setEnvironment({ shadow: { ...shadow, ...patch } });
  }, [setEnvironment, shadow]);

  const setFog = useCallback((patch: Partial<typeof fog>) => {
    setEnvironment({ fog: { ...fog, ...patch } });
  }, [setEnvironment, fog]);

  const handleUpload = useCallback(async (file: File) => {
    const ext = file.name.split('.').pop()?.toLowerCase();
    if (!['hdr', 'exr', 'jpg', 'jpeg', 'png'].includes(ext || '')) {
      message.error('支持格式: .hdr, .exr, .jpg, .png');
      return false;
    }
    setUploading(true);
    setProgress(0);
    try {
      const { url } = await uploadEnvMap(file, setProgress);
      setEnvironment({ environmentMapUrl: url });
      message.success('环境贴图加载成功');
    } catch (e: any) {
      message.error(e?.response?.data?.message || '上传失败');
    } finally {
      setUploading(false);
    }
    return false;
  }, [setEnvironment]);

  // 「重置本组」：写回该组默认值（store 默认 + 场景默认）
  const resetBasic = useCallback(() => {
    setBoundedMode(GROUP_DEFAULTS.basic.boundedMode);
    setShowGround(GROUP_DEFAULTS.basic.showGround);
    setShowGrid(GROUP_DEFAULTS.basic.showGrid);
    setEnvironment({ backgroundColor: GROUP_DEFAULTS.basic.backgroundColor });
    setCameraStrategy(GROUP_DEFAULTS.basic.cameraStrategy);
  }, [setBoundedMode, setShowGround, setShowGrid, setEnvironment, setCameraStrategy]);

  const resetSky = useCallback(() => {
    setEffects({ sky: { ...sky, ...GROUP_DEFAULTS.sky } });
  }, [setEffects, sky]);

  const resetLight = useCallback(() => {
    setEnvironment({ ...GROUP_DEFAULTS.light });
  }, [setEnvironment]);

  const resetShadow = useCallback(() => {
    setEnvironment({ shadow: { ...DEFAULT_SHADOW_SETTINGS } });
  }, [setEnvironment]);

  const resetFog = useCallback(() => {
    setEnvironment({ ...GROUP_DEFAULTS.fog });
  }, [setEnvironment]);

  const resetBtn = (testid: string, onClick: () => void) => (
    <Button
      size="small"
      type="text"
      data-testid={testid}
      icon={<ReloadOutlined />}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
    >
      重置本组
    </Button>
  );

  const items = useMemo(() => [
    {
      key: 'basic',
      label: '基础设置',
      extra: resetBtn('ssp-reset-basic', resetBasic),
      children: (
        <Form layout="vertical" size="small">
          <Field
            label="环境空间模式"
            tooltip={'无限空间=可自由浏览到任意远处；有限空间=相机被限制在模型区域内。\n使用案例：室内展厅用「有限空间」防止镜头穿到室外。'}
          >
            <Select
              size="small"
              data-testid="ssp-space-mode"
              style={{ width: '100%' }}
              value={boundedMode ? 'bounded' : 'infinite'}
              onChange={(v) => setBoundedMode(v === 'bounded')}
              options={[{ value: 'infinite', label: '无限空间' }, { value: 'bounded', label: '有限空间' }]}
            />
          </Field>
          <Field label="显示地面" tooltip={'控制 Y=0 地面网格平面是否渲染（阴影需要地面承接）。\n使用案例：做纯模型展示截图时隐藏地面。'}>
            <Switch size="small" data-testid="ssp-ground" checked={showGround} onChange={setShowGround} />
          </Field>
          <Field label="显示网格" tooltip={'控制参考网格（含坐标轴）显示。\n使用案例：布置点位时打开网格便于对齐。'}>
            <Switch size="small" data-testid="ssp-grid" checked={showGrid} onChange={setShowGrid} />
          </Field>
          <Field label="网格格子" tooltip={'自动=格子随相机远近自动细分（1/2/5×10ⁿ 档位）；手动=固定格子大小。\n使用案例：大型厂区固定 10 单位格子便于估算尺寸。'}>
            <Space size={4}>
              <Switch size="small" data-testid="ssp-grid-auto" checked={gridAuto} onChange={(v) => useEditorUiStore.getState().setGridAuto(v)} />
              <Text style={{ fontSize: 11, color: '#888' }}>自动</Text>
              <InputNumber
                size="small"
                min={0.01}
                step={1}
                style={{ width: 76 }}
                disabled={gridAuto}
                value={gridCellSize}
                onChange={(v) => useEditorUiStore.getState().setGridCellSize(v ?? 10)}
              />
            </Space>
          </Field>
          <Field
            label="自动适配相机"
            tooltip={'开启=模型加载/导入后相机自动对准模型；关闭=保持当前视角。\n使用案例：对齐多模型时关闭，避免每次导入都跳视角。'}
          >
            <Switch
              size="small"
              data-testid="ssp-autofit-camera"
              checked={cameraStrategy === 'auto'}
              onChange={(v) => setCameraStrategy(v ? 'auto' : 'current')}
            />
          </Field>
          <Field label="世界原点 (X/Y/Z)" tooltip={'导航中心（定位/自动适配/自动旋转等以此为参考点；远坐标场景可把世界原点设到厂区坐标上）。\n使用案例：厂区实际坐标 (1000,0,500) 时设世界原点，导航更自然。'}>
            <Space size={6}>
              <InputNumber size="small" id="ssp-world-origin-x" style={{ width: 78 }} step={10} value={environment.worldOrigin?.[0] ?? 0} onChange={(v) => setEnvironment({ worldOrigin: [Number(v) || 0, environment.worldOrigin?.[1] ?? 0, environment.worldOrigin?.[2] ?? 0] })} />
              <InputNumber size="small" id="ssp-world-origin-y" style={{ width: 78 }} step={10} value={environment.worldOrigin?.[1] ?? 0} onChange={(v) => setEnvironment({ worldOrigin: [environment.worldOrigin?.[0] ?? 0, Number(v) || 0, environment.worldOrigin?.[2] ?? 0] })} />
              <InputNumber size="small" id="ssp-world-origin-z" style={{ width: 78 }} step={10} value={environment.worldOrigin?.[2] ?? 0} onChange={(v) => setEnvironment({ worldOrigin: [environment.worldOrigin?.[0] ?? 0, environment.worldOrigin?.[1] ?? 0, Number(v) || 0] })} />
            </Space>
          </Field>
          <Field label="背景颜色" tooltip={'画布背景色（天空未启用时可见）。\n使用案例：夜景大屏用深蓝 #0a1024 作为底色。'}>
            <ColorPicker
              data-testid="ssp-bg-color"
              value={environment.backgroundColor}
              onChange={(c) => setEnvironment({ backgroundColor: c.toHexString() })}
              showText
            />
          </Field>
        </Form>
      ),
    },
    {
      key: 'sky',
      label: '天空',
      extra: resetBtn('ssp-reset-sky', resetSky),
      children: (
        <Form layout="vertical" size="small">
          <Field label="启用天空" tooltip={'程序化天空（大气散射）。启用后太阳位置由「太阳时间/高度角」驱动。\n使用案例：室外场景开启天空，昼夜效果更真实。'}>
            <Switch size="small" data-testid="ssp-sky-enabled" checked={sky.enabled} onChange={(v) => setEffects({ sky: { ...sky, enabled: v } })} />
          </Field>
          <Field label="类型" tooltip={'程序化=渐变+太阳（默认）；贴图=上传/填写 HDR 或全景图；纯色=单色背景。\n使用案例：客户提供全景图时用「贴图」。'}>
            <Select
              size="small"
              data-testid="ssp-sky-type"
              style={{ width: '100%' }}
              value={sky.type ?? 'procedural'}
              onChange={(v) => setEffects({ sky: { ...sky, type: v } })}
              options={[{ value: 'procedural', label: '程序化' }, { value: 'texture', label: '贴图' }, { value: 'color', label: '纯色' }]}
            />
          </Field>
          {(sky.type ?? 'procedural') === 'color' && (
            <Field label="天空颜色" tooltip={'纯色天空颜色（与不透明度叠加在背景色上）。\n使用案例：演示模式用品牌主色。'}>
              <ColorPicker
                data-testid="ssp-sky-color"
                value={sky.color ?? '#87CEEB'}
                onChange={(c) => setEffects({ sky: { ...sky, color: c.toHexString() } })}
                showText
              />
            </Field>
          )}
          {(sky.type ?? 'procedural') === 'texture' && (
            <>
              <Field label="贴图类型" tooltip={'HDR=环境光照贴图（.hdr，等距柱状）；六面立方体=skybox 六张图。\n使用案例：HDR 提供真实光照反射；六面贴图用美术出的 skybox。'}>
                <Select
                  size="small"
                  data-testid="ssp-sky-texture-type"
                  style={{ width: '100%' }}
                  value={sky.textureType ?? 'hdr'}
                  onChange={(v) => setEffects({ sky: { ...sky, textureType: v } })}
                  options={[{ value: 'hdr', label: 'HDR（等距柱状）' }, { value: 'cube', label: '六面立方体（skybox）' }]}
                />
              </Field>
              <Field label="贴图地址" tooltip={'HDR：单个 .hdr 地址；六面：6 个地址逗号分隔（pz,nz,px,nx,py,ny）或以 / 结尾的目录前缀（自动拼 px/nx/py/ny/pz/nz + .jpg）。\n使用案例：/uploads/env/sky.hdr 或 /assets/skybox/。'}>
                <Input
                  size="small"
                  data-testid="ssp-sky-texture-url"
                  placeholder="https://... 或 /uploads/...（六面用逗号分隔）"
                  value={sky.textureUrl ?? ''}
                  onChange={(e) => setEffects({ sky: { ...sky, textureUrl: e.target.value } })}
                />
              </Field>
            </>
          )}
          <Field label={`不透明度 ${(sky.opacity ?? 1).toFixed(2)}`} tooltip={'天空与背景色的混合比例（1=完全天空，0=仅背景色）。\n使用案例：半透明天空与地形雾衔接。'}>
            <Slider
              data-testid="ssp-sky-opacity"
              min={0}
              max={1}
              step={0.05}
              value={sky.opacity ?? 1}
              onChange={(v) => setEffects({ sky: { ...sky, opacity: v } })}
            />
          </Field>
          <Field label={`亮度 ${(sky.textureIntensity ?? 1).toFixed(2)}`} tooltip={'天空整体亮度（HDR/六面贴图经 backgroundIntensity；程序化则压暗/提亮渐变）。\n使用案例：夜景 0.4 压暗天空。'}>
            <Slider
              data-testid="ssp-sky-texture-intensity"
              min={0.2}
              max={2}
              step={0.05}
              value={sky.textureIntensity ?? 1}
              onChange={(v) => setEffects({ sky: { ...sky, textureIntensity: v } })}
            />
          </Field>
          <Field label={`太阳光晕 ${(sky.lensFlareIntensity ?? 0.6).toFixed(2)}`} tooltip={'程序化太阳光晕强度（0=关闭光晕）。\n使用案例：黄昏/晴天增强太阳存在感。'}>
            <Slider
              data-testid="ssp-sky-flare"
              min={0}
              max={1}
              step={0.05}
              value={sky.lensFlareIntensity ?? 0.6}
              onChange={(v) => setEffects({ sky: { ...sky, lensFlareIntensity: v } })}
            />
          </Field>
          <Field label="时间模式" tooltip={'自动=跟随系统时钟（实时昼夜，不写入场景）；手动=用下方滑杆指定时间。\n使用案例：演示时用自动，出图时手动固定 16:00。'}>
            <Segmented
              size="small"
              data-testid="ssp-sky-time-mode"
              value={sky.timeMode ?? 'auto'}
              onChange={(v) => setEffects({ sky: { ...sky, timeMode: v as 'auto' | 'manual' } })}
              options={[{ value: 'auto', label: '自动' }, { value: 'manual', label: '手动' }]}
            />
          </Field>
          <Field
            label={`太阳时间 ${formatTime(sky.timeHours ?? 12)}`}
            tooltip={'手动时间（0~24h）：6:00 日出 / 12:00 正午 / 18:00 日落 / 夜间太阳低于地平线。\n使用案例：定 17:30 出黄昏光。'}
          >
            <Slider
              data-testid="ssp-sky-time"
              min={0}
              max={24}
              step={0.25}
              value={sky.timeHours ?? 12}
              disabled={(sky.timeMode ?? 'auto') !== 'manual'}
              onChange={(v) => {
                const sun = sunFromTime(v);
                setEffects({ sky: { ...sky, timeMode: 'manual', timeHours: v, sunElevation: sun.elevation, sunAzimuth: sun.azimuth } });
              }}
            />
          </Field>
          <Field label={`太阳高度角 ${(sky.sunElevation * 100).toFixed(0)}%`} tooltip={'0=地平线（黄昏/清晨，长影），100=正午（顶光，短影）。拖动会切换到手动时间。\n使用案例：拍建筑外观用 30% 拉长阴影更有立体感。'}>
            <Slider
              data-testid="ssp-sun-elevation"
              min={0}
              max={1}
              step={0.01}
              value={sky.sunElevation}
              disabled={!sky.enabled}
              onChange={(v) => setEffects({ sky: { ...sky, timeMode: 'manual', sunElevation: v } })}
            />
          </Field>
          <Field label={`太阳方位角 ${(sky.sunAzimuth * 360).toFixed(0)}°`} tooltip={'绕竖直轴旋转太阳方向（0~360°）。拖动会切换到手动时间。\n使用案例：把太阳转到模型左前方，让主立面受光。'}>
            <Slider
              data-testid="ssp-sun-azimuth"
              min={0}
              max={1}
              step={0.01}
              value={sky.sunAzimuth}
              disabled={!sky.enabled}
              onChange={(v) => setEffects({ sky: { ...sky, timeMode: 'manual', sunAzimuth: v } })}
            />
          </Field>
          <Field label="星空" tooltip={'夜空星空（太阳低于地平线后渐显）。\n使用案例：夜景大屏 + 星空氛围。'}>
            <Switch size="small" data-testid="ssp-stars-enabled" checked={sky.starsEnabled === true} onChange={(v) => setEffects({ sky: { ...sky, starsEnabled: v } })} />
          </Field>
          {sky.starsEnabled === true && (
            <>
              <Field label={`星密度 ${sky.starDensity ?? 3000}`} tooltip={'星星数量。\n使用案例：4000~6000 更密。'}>
                <Slider min={500} max={8000} step={100} value={sky.starDensity ?? 3000} onChange={(v) => setEffects({ sky: { ...sky, starDensity: v } })} />
              </Field>
              <Field label={`月相 ${(sky.moonPhase ?? 0.5).toFixed(2)}（0=新月 0.5=满月）`} tooltip={'月亮盈亏相位。\n使用案例：0.5 满月 / 0.25 半月。'}>
                <Slider min={0} max={1} step={0.01} value={sky.moonPhase ?? 0.5} onChange={(v) => setEffects({ sky: { ...sky, moonPhase: v } })} />
              </Field>
              <Field label={`月光强度 ${(sky.moonIntensity ?? 0).toFixed(2)}`} tooltip={'0=不显示月亮。\n使用案例：0.6 明亮满月。'}>
                <Slider min={0} max={1} step={0.05} value={sky.moonIntensity ?? 0} onChange={(v) => setEffects({ sky: { ...sky, moonIntensity: v } })} />
              </Field>
            </>
          )}
          <Field label="天气预置" tooltip={'一键组合天空时间+光照+雾+粒子（可再微调）。\n使用案例：出图前快速切「黄昏」「雨天」。'}>
            <Space size={4} wrap data-testid="ssp-weather-presets">
              {WEATHER_PRESETS.map((p) => (
                <Button key={p.key} size="small" data-testid={`ssp-weather-${p.key}`} onClick={() => p.apply()}>
                  {p.label}
                </Button>
              ))}
            </Space>
          </Field>
        </Form>
      ),
    },
    {
      key: 'light',
      label: '光照',
      extra: resetBtn('ssp-reset-light', resetLight),
      children: (
        <Form layout="vertical" size="small">
          <Field label={`环境光强度 ${environment.ambientIntensity.toFixed(2)}`} tooltip={'环境光=均匀底色光，避免背光面全黑。\n使用案例：室内场景环境光 0.6~0.8，过亮会削弱阴影层次。'}>
            <Slider
              data-testid="ssp-ambient-intensity"
              min={0}
              max={2}
              step={0.05}
              value={environment.ambientIntensity}
              onChange={(v) => setEnvironment({ ambientIntensity: v })}
            />
          </Field>
          <Field label="环境光颜色" tooltip={'环境光色调（冷色偏夜景/工业，暖色偏室内）。\n使用案例：冷库场景用 #dbe9ff 冷白。'}>
            <ColorPicker
              data-testid="ssp-ambient-color"
              value={environment.ambientColor}
              onChange={(c) => setEnvironment({ ambientColor: c.toHexString() })}
              showText
            />
          </Field>
          <Field label={`平行光(太阳)强度 ${environment.directionalIntensity.toFixed(2)}`} tooltip={'主光方向光强度，决定阴影对比度；为 0 时阴影随之关闭。\n使用案例：产品展示用 1.2~1.6 获得清晰投影。'}>
            <Slider
              data-testid="ssp-directional-intensity"
              min={0}
              max={3}
              step={0.05}
              value={environment.directionalIntensity}
              onChange={(v) => setEnvironment({ directionalIntensity: v })}
            />
          </Field>
          <Field label="平行光(太阳)颜色" tooltip={'主光色温：低角度偏暖（日出/日落），正午偏白。\n使用案例：黄昏场景 #ffd9a0。'}>
            <ColorPicker
              data-testid="ssp-directional-color"
              value={environment.directionalColor}
              onChange={(c) => setEnvironment({ directionalColor: c.toHexString() })}
              showText
            />
          </Field>
          <Field label="环境贴图 (HDR/EXR)" tooltip={'环境贴图提供真实反射与间接光（IBL）。\n使用案例：金属/玻璃材质上传 HDR 后反射更真实。'}>
            <div>
              {environment.environmentMapUrl ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} />
                  <Text style={{ fontSize: 11, color: '#52c41a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {environment.environmentMapUrl.split('/').pop()}
                  </Text>
                  <Button
                    size="small"
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => setEnvironment({ environmentMapUrl: null })}
                    danger
                  />
                </div>
              ) : (
                <Upload accept=".hdr,.exr,.jpg,.jpeg,.png" showUploadList={false} beforeUpload={handleUpload} disabled={uploading}>
                  <Button size="small" icon={<UploadOutlined />} loading={uploading}>上传环境贴图</Button>
                </Upload>
              )}
              {uploading && <Progress percent={progress} size="small" style={{ marginTop: 4 }} />}
            </div>
          </Field>
        </Form>
      ),
    },
    {
      key: 'shadow',
      label: '阴影',
      extra: resetBtn('ssp-reset-shadow', resetShadow),
      children: (
        <Form layout="vertical" size="small">
          <Field label="自动适配" tooltip={'自动=阴影相机范围随场景半径放大（大场景也有影子）；关闭=手动指定范围。\n使用案例：厂区整体 500m 用自动；只关注单台设备时手动 20m 更清晰。'}>
            <Switch size="small" data-testid="ssp-shadow-auto" checked={shadow.auto} onChange={(v) => setShadow({ auto: v })} />
          </Field>
          <Field label="阴影范围 (m)" tooltip={'阴影相机覆盖的半径（仅「自动适配」关闭时生效）。范围越大越容易出现锯齿。\n使用案例：单台设备 20m，车间 80m。'}>
            <InputNumber
              size="small"
              data-testid="ssp-shadow-radius"
              style={{ width: '100%' }}
              min={10}
              max={3000}
              step={10}
              disabled={shadow.auto}
              value={shadow.radius}
              onChange={(v) => setShadow({ radius: Number(v) || DEFAULT_SHADOW_SETTINGS.radius })}
            />
          </Field>
          <Field label="阴影分辨率" tooltip={'贴图分辨率越高阴影边缘越锐利，显存/性能开销越大。\n使用案例：交付大屏用 4096，编辑期 1024 提帧。'}>
            <Select
              size="small"
              data-testid="ssp-shadow-map-size"
              style={{ width: '100%' }}
              value={shadow.mapSize}
              onChange={(v) => setShadow({ mapSize: v })}
              options={[{ value: 1024, label: '1024（快）' }, { value: 2048, label: '2048（推荐）' }, { value: 4096, label: '4096（精细）' }]}
            />
          </Field>
          <Field label={`阴影强度 ${shadow.intensity.toFixed(2)}`} tooltip={'0=不投影（性能最优）；1=完整阴影。\n使用案例：批量布置时可临时调到 0 提升帧率。'}>
            <Slider
              data-testid="ssp-shadow-intensity"
              min={0}
              max={1}
              step={0.05}
              value={shadow.intensity}
              onChange={(v) => setShadow({ intensity: v })}
            />
          </Field>
          <Field label="阴影偏移 (bias)" tooltip={'消除阴影自遮挡产生的条纹（shadow acne）；负值偏移越大越容易漏光。\n使用案例：薄片/大面积地板出现条纹时调到 -0.002。'}>
            <InputNumber
              size="small"
              data-testid="ssp-shadow-bias"
              style={{ width: '100%' }}
              step={0.0005}
              value={shadow.bias}
              onChange={(v) => setShadow({ bias: Number(v) })}
            />
          </Field>
        </Form>
      ),
    },
    {
      key: 'fog',
      label: '雾',
      extra: resetBtn('ssp-reset-fog', resetFog),
      children: (
        <Form layout="vertical" size="small">
          <Field label="启用雾" tooltip={'线性雾：远处淡入雾色，用于隐藏场景边界、营造纵深。\n使用案例：室外园区开启雾让远处地平线柔和过渡。'}>
            <Switch size="small" data-testid="ssp-fog-enabled" checked={environment.fogEnabled} onChange={(v) => setEnvironment({ fogEnabled: v })} />
          </Field>
          <Field label="雾颜色" tooltip={'雾色应与天空/背景协调。\n使用案例：白天 #c8e4f8，黄昏 #f0c9a0。'}>
            <ColorPicker
              data-testid="ssp-fog-color"
              value={fog.color}
              disabled={!environment.fogEnabled}
              onChange={(c) => setFog({ color: c.toHexString() })}
              showText
            />
          </Field>
          <Field label={`雾浓度 ${fog.density.toFixed(3)}`} tooltip={'浓度越大雾越近越浓（同时影响网格/地面的可见范围）。\n使用案例：0.008 默认，室内 0.002 轻微，室外大场景 0.02 强纵深。'}>
            <Slider
              data-testid="ssp-fog-density"
              min={0}
              max={0.1}
              step={0.001}
              value={fog.density}
              disabled={!environment.fogEnabled}
              onChange={(v) => setFog({ density: v })}
            />
          </Field>
          <Field label="起点距离 (m，0=自动)" tooltip={'雾开始出现的距离（留 0 = 自动随场景半径）。\n使用案例：只看近处设备时设 30，远处保持清晰。'}>
            <InputNumber
              size="small"
              id="ssp-fog-start"
              style={{ width: '100%' }}
              min={0}
              max={100000}
              step={5}
              disabled={!environment.fogEnabled}
              value={fog.startDistance}
              onChange={(v) => setFog({ startDistance: Number(v) || 0 })}
            />
          </Field>
          <Field label="终点距离 (m，0=自动)" tooltip={'雾达到最大浓度的距离（留 0 = 自动随场景半径）。\n使用案例：园区 400m 处完全没入雾中。'}>
            <InputNumber
              size="small"
              id="ssp-fog-end"
              style={{ width: '100%' }}
              min={0}
              max={100000}
              step={10}
              disabled={!environment.fogEnabled}
              value={fog.endDistance}
              onChange={(v) => setFog({ endDistance: Number(v) || 0 })}
            />
          </Field>
          <Field label={`最大不透明度 ${fog.maxOpacity.toFixed(2)}`} tooltip={'雾最浓处的不透明度（<1 时远景仍隐约可见）。\n使用案例：0.8 保持远景轮廓，1 完全遮住边界。'}>
            <Slider
              data-testid="ssp-fog-max-opacity"
              min={0.1}
              max={1}
              step={0.05}
              value={fog.maxOpacity}
              disabled={!environment.fogEnabled}
              onChange={(v) => setFog({ maxOpacity: v })}
            />
          </Field>
          <Field label="雾色跟随天空/背景" tooltip={'开启后雾色自动使用背景色（换天空/夜景时雾色同步）。\n使用案例：昼夜切换场景避免雾色违和。'}>
            <Switch
              size="small"
              data-testid="ssp-fog-follow-sky"
              checked={fog.affectedBySky}
              disabled={!environment.fogEnabled}
              onChange={(v) => setFog({ affectedBySky: v })}
            />
          </Field>
          <Text style={{ fontSize: 11, color: '#888' }}>高度衰减 / 高度偏移（高度雾）在 P2 提供</Text>
        </Form>
      ),
    },
    {
      key: 'render',
      label: '渲染',
      children: (
        <Form layout="vertical" size="small">
          <Field label="性能档" tooltip={'一键批改：高（SSAO 开/阴影 4096）/ 中（抗锯齿/阴影 2048）/ 低（分辨率 0.75/全关/阴影 1024）。\n使用案例：交付前切「高」，低配演示机切「低」。'}>
            <Select
              size="small"
              data-testid="ssp-performance-tier"
              style={{ width: '100%' }}
              placeholder="选择档位（不改动=自定义）"
              value={perfTier}
              onChange={(tier: unknown) => {
                const t = tier as PerformanceTier;
                setPerfTier(t);
                const patch = performanceTierPatch(t);
                setEffects({ postprocessing: patch.postprocessing as never });
                setEnvironment({ shadow: { ...shadow, mapSize: patch.shadow.mapSize } });
                // S4：雾盒 raymarch 步数联动（应用到全部雾盒）
                volumes.filter((v) => v.kind === 'fogBox').forEach((v) => {
                  updateVolume(v.id, { vol: { density: 0.35, color: '#cfe6f5', noiseScale: 1, noiseSpeed: 0.25, opacity: 0.55, ...(v.vol || {}), steps: patch.volumetrics.fogSteps } });
                });
                message.success(`已应用性能档：${{ high: '高', medium: '中', low: '低' }[t]}`);
              }}
              options={[{ value: 'high', label: '高（精细）' }, { value: 'medium', label: '中（均衡）' }, { value: 'low', label: '低（流畅）' }]}
            />
          </Field>
          <Field label={`分辨率缩放 ${(postprocessing.resolutionScale ?? 1).toFixed(2)}`} tooltip={'内部渲染分辨率倍数（<1 提帧更模糊、>1 更清晰但更吃性能；1=按设备像素比，上限 2）。\n使用案例：低配机器用 0.75 提升流畅度；出图前用 1.5 更锐利。'}>
            <Slider
              data-testid="ssp-resolution-scale"
              min={0.5}
              max={2}
              step={0.05}
              value={postprocessing.resolutionScale ?? 1}
              onChange={(v) => setEffects({ postprocessing: { resolutionScale: v } as never })}
            />
          </Field>
          <Field label="环境光遮蔽 SSAO" tooltip={'Screen-Space Ambient Occlusion：增强缝隙/角落的接触阴影。\n使用案例：工业设备模型开启后结构更立体（性能开销较大）。'}>
            <Switch size="small" data-testid="ssp-ssao-enabled" checked={postprocessing.ssao.enabled} onChange={(v) => setEffects({ postprocessing: { ssao: { enabled: v } } as never })} />
          </Field>
          <Field label="抗锯齿（SMAA）" tooltip={'后处理抗锯齿（开启时关闭 MSAA 以省显存）。\n使用案例：细密栅格/薄片边缘闪烁时开启。'}>
            <Switch size="small" data-testid="ssp-smaa-enabled" checked={postprocessing.antialiasing.enabled} onChange={(v) => setEffects({ postprocessing: { antialiasing: { enabled: v } } as never })} />
          </Field>
          <Field label="硬件负载" tooltip={'按最近帧耗时（EMA）估算：绿=流畅、黄=一般、红=吃力。降低分辨率缩放/关闭 SSAO/降低阴影分辨率可提帧。'}>
            <Space size={8} data-testid="ssp-load-badge">
              <Tag color={loadColor} icon={<ThunderboltOutlined />} style={{ marginInlineEnd: 0 }}>{loadText}</Tag>
              <Text style={{ fontSize: 12, color: '#bbb' }} data-testid="ssp-load-fps">≈{fps} FPS（{frameMs.toFixed(1)} ms/帧）</Text>
            </Space>
          </Field>
        </Form>
      ),
    },
    {
      key: 'post',
      label: '后期',
      children: (
        <Form layout="vertical" size="small">
          <Field label={`曝光 ${(postprocessing.exposure ?? 1.2).toFixed(2)}`} tooltip={'整体画面亮度（色调映射曝光）。\n使用案例：夜景大屏调 0.8 压暗，暗部细节更稳。'}>
            <Slider
              data-testid="ssp-exposure"
              min={0.2}
              max={3}
              step={0.05}
              value={postprocessing.exposure ?? 1.2}
              onChange={(v) => setEffects({ postprocessing: { exposure: v } as never })}
            />
          </Field>
          <Field label="泛光 Bloom" tooltip={'亮部溢光（霓虹/灯光更亮眼）。\n使用案例：大屏灯光/告警闪烁增强氛围。'}>
            <Switch size="small" data-testid="ssp-bloom-enabled" checked={postprocessing.bloom.enabled} onChange={(v) => setEffects({ postprocessing: { bloom: { enabled: v } } as never })} />
          </Field>
          {postprocessing.bloom.enabled && (
            <>
              <Field label={`泛光强度 ${postprocessing.bloom.intensity.toFixed(2)}`} tooltip={'溢光强度（0~2）。\n使用案例：0.3~0.6 轻微辉光。'}>
                <Slider min={0} max={2} step={0.05} value={postprocessing.bloom.intensity} onChange={(v) => setEffects({ postprocessing: { bloom: { intensity: v } } as never })} />
              </Field>
              <Field label={`泛光阈值 ${postprocessing.bloom.threshold.toFixed(2)}`} tooltip={'触发溢光的亮度阈值（越低越多区域发光）。\n使用案例：只让灯光发光用 0.8~0.9。'}>
                <Slider min={0} max={1} step={0.05} value={postprocessing.bloom.threshold} onChange={(v) => setEffects({ postprocessing: { bloom: { threshold: v } } as never })} />
              </Field>
            </>
          )}
          <Field label="景深 DOF" tooltip={'近/远景虚化（出图电影感）。\n使用案例：产品特写聚焦主体。'}>
            <Switch size="small" data-testid="ssp-dof-enabled" checked={postprocessing.dof.enabled} onChange={(v) => setEffects({ postprocessing: { dof: { enabled: v } } as never })} />
          </Field>
          <Field label="噪点颗粒" tooltip={'画面叠加细噪点（胶片感，可掩盖色带）。\n使用案例：暗部大面积渐变出现色带时开启 0.05。'}>
            <Switch size="small" data-testid="ssp-grain-enabled" checked={postprocessing.grain.enabled} onChange={(v) => setEffects({ postprocessing: { grain: { enabled: v } } as never })} />
          </Field>
          <Field label="暗角 Vignette" tooltip={'四角压暗（聚焦画面中心）。\n使用案例：大屏主视觉增强聚焦。'}>
            <Switch size="small" data-testid="ssp-vignette-enabled" checked={postprocessing.vignette.enabled} onChange={(v) => setEffects({ postprocessing: { vignette: { enabled: v } } as never })} />
          </Field>
        </Form>
      ),
    },
    {
      key: 'volume',
      label: '体积',
      children: (
        <Form layout="vertical" size="small">
          <Space size={6} wrap style={{ marginBottom: 8 }}>
            <Button size="small" data-testid="vol-add-env" onClick={() => void tryAddVolume({ kind: 'envOverride', name: `区域覆盖 ${volumes.length + 1}`, enabled: true, shape: 'box', position: [0, 10, 0], size: [30, 40, 30], blendDistance: 15, priority: 0, envs: { ambientIntensity: 0.9, fogDensity: 0.03 } })}>+ 区域覆盖</Button>
            <Button size="small" data-testid="vol-add-ray" onClick={() => void tryAddVolume({ kind: 'godRay', name: `光柱 ${volumes.length + 1}`, enabled: true, shape: 'cylinder', position: [0, 0, 0], size: [6, 30, 6], ray: { color: '#a0d8ff', intensity: 0.6, noiseSpeed: 0.3, noiseScale: 1 } })}>+ 光柱</Button>
            <Button size="small" data-testid="vol-add-fogbox" onClick={() => void tryAddVolume({ kind: 'fogBox', name: `雾盒 ${volumes.length + 1}`, enabled: true, shape: 'box', position: [0, 8, 0], size: [12, 6, 12], vol: { density: 0.35, color: '#cfe6f5', noiseScale: 1, noiseSpeed: 0.25, opacity: 0.55, steps: 32 } })}>+ 雾盒</Button>
            <Button size="small" data-testid="vol-add-cloud" onClick={() => void tryAddVolume({ kind: 'cloudImpostor', name: `云团 ${volumes.length + 1}`, enabled: true, shape: 'box', position: [0, 12, 0], size: [10, 5, 10], vol: { density: 0.5, color: '#ffffff', noiseScale: 1, noiseSpeed: 0.3, opacity: 0.5 } })}>+ 云团</Button>
          </Space>
          {/* M3：体积预设（市场下发；免费/付费/积分） */}
          <div data-testid="vol-preset-section" style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, color: '#999' }}>体积预设（市场）</Text>
            {volPresets.length === 0 ? (
              <Text style={{ fontSize: 11, color: '#666', display: 'block' }}>暂无可用预设（推广后台「组件管理」上传并上架后在此应用）</Text>
            ) : volPresets.map((t, ti) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Text style={{ fontSize: 12, color: '#ddd', flex: 1 }} ellipsis>{t.name}</Text>
                <Tag color={presetPriceLabel(t).color} style={{ marginInlineEnd: 0 }}>{presetPriceLabel(t).text}</Tag>
                <Button size="small" data-testid={`vol-preset-apply-${ti}`} onClick={() => void applyMarketPreset(t, 'volume')}>应用</Button>
              </div>
            ))}
          </div>
          {volumes.length === 0 && <Text style={{ fontSize: 11, color: '#888' }}>暂无体积元素（区域覆盖=进入区域改变氛围；光柱=可见体积光；雾盒/云团=可见体积体）</Text>}
          {volumes.map((v, i) => (
            <div key={v.id} data-testid={`vol-card-${i}`} style={{ border: volumeSelectedId === v.id ? '1px solid #ffd666' : '1px solid #30475e', borderRadius: 4, padding: 6, marginBottom: 6 }}>
              <Space size={6} style={{ marginBottom: 4 }}>
                <Tag color={v.kind === 'envOverride' ? 'cyan' : v.kind === 'godRay' ? 'gold' : v.kind === 'fogBox' ? 'blue' : 'purple'} style={{ marginInlineEnd: 0 }}>{v.kind === 'envOverride' ? '区域覆盖' : v.kind === 'godRay' ? '光柱' : v.kind === 'fogBox' ? '雾盒' : '云团'}</Tag>
                <Switch size="small" data-testid={`vol-enabled-${i}`} checked={v.enabled} onChange={(val) => updateVolume(v.id, { enabled: val })} />
                <Button size="small" type="text" data-testid={`vol-select-${i}`} icon={<AimOutlined />} onClick={() => selectVolume(v.id)} />
                <Button size="small" type="text" danger data-testid={`vol-del-${i}`} icon={<DeleteOutlined />} onClick={() => removeVolume(v.id)} />
              </Space>
              <Space size={6} style={{ marginBottom: 4 }}>
                <Input size="small" style={{ width: 120 }} value={v.name} onChange={(e) => updateVolume(v.id, { name: e.target.value })} />
                {/* 雾盒/云团为盒体实现（raymarch AABB / 贴片盒内分布）→ 形状固定「盒」 */}
                {v.kind === 'fogBox' || v.kind === 'cloudImpostor' ? (
                  <Text style={{ fontSize: 11, color: '#999', width: 84 }}>盒（体积体固定）</Text>
                ) : (
                  <Select size="small" style={{ width: 84 }} value={v.shape} onChange={(shape) => updateVolume(v.id, { shape })} options={[{ value: 'box', label: '盒' }, { value: 'sphere', label: '球' }, { value: 'cylinder', label: '柱' }]} />
                )}
              </Space>
              <Space size={4} style={{ marginBottom: 4 }}>
                {([0, 1, 2] as const).map((idx) => (
                  <InputNumber key={`p${idx}`} size="small" id={`vol-pos-${i}-${'xyz'[idx]}`} style={{ width: 62 }} step={5} value={v.position[idx]} onChange={(val) => { const p = [...v.position] as [number, number, number]; p[idx] = Number(val) || 0; updateVolume(v.id, { position: p }); }} />
                ))}
                {([0, 1, 2] as const).map((idx) => (
                  <InputNumber key={`s${idx}`} size="small" id={`vol-size-${i}-${'xyz'[idx]}`} style={{ width: 62 }} step={5} min={0.5} value={v.size[idx]} onChange={(val) => { const s = [...v.size] as [number, number, number]; s[idx] = Math.max(0.5, Number(val) || 0.5); updateVolume(v.id, { size: s }); }} />
                ))}
              </Space>
              <Space size={6} style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>混合距离</Text>
                <InputNumber size="small" id={`vol-blend-${i}`} style={{ width: 70 }} min={0} step={5} value={v.blendDistance ?? 0} onChange={(val) => updateVolume(v.id, { blendDistance: Number(val) || 0 })} />
                <Text style={{ fontSize: 11, color: '#999' }}>优先级</Text>
                <InputNumber size="small" style={{ width: 56 }} min={0} step={1} value={v.priority ?? 0} onChange={(val) => updateVolume(v.id, { priority: Number(val) || 0 })} />
              </Space>
              {v.kind === 'envOverride' && (
                <>
                  <Field label={`环境光强度 ${Number(v.envs?.ambientIntensity ?? environment.ambientIntensity).toFixed(2)}（区域内）`} tooltip={'进入区域后的环境光强度（Unity Volume 语义：区域内外按混合距离插值）。\n使用案例：进入机房区域整体压暗/提亮。'}>
                    <Slider min={0} max={2} step={0.05} value={Number(v.envs?.ambientIntensity ?? environment.ambientIntensity)} onChange={(val) => updateVolume(v.id, { envs: { ...(v.envs || {}), ambientIntensity: val } })} />
                  </Field>
                  <Field label={`雾浓度 ${Number(v.envs?.fogDensity ?? fog.density).toFixed(3)}（区域内）`} tooltip={'进入区域后的雾浓度（>0 时区域内自动开雾）。\n使用案例：进入地下管廊区域加雾。'}>
                    <Slider min={0} max={0.1} step={0.001} value={Number(v.envs?.fogDensity ?? 0)} onChange={(val) => updateVolume(v.id, { envs: { ...(v.envs || {}), fogDensity: val, fogEnabled: val > 0 } })} />
                  </Field>
                  <Field label={`曝光 ${Number(v.envs?.exposure ?? (postprocessing.exposure ?? 1.2)).toFixed(2)}（区域内）`} tooltip={'进入区域后的曝光。\n使用案例：隧道口进入后暗适应。'}>
                    <Slider min={0.2} max={3} step={0.05} value={Number(v.envs?.exposure ?? postprocessing.exposure ?? 1.2)} onChange={(val) => updateVolume(v.id, { envs: { ...(v.envs || {}), exposure: val } })} />
                  </Field>
                </>
              )}
              {v.kind === 'godRay' && (
                <>
                  <Field label={`光柱强度 ${Number(v.ray?.intensity ?? 0.6).toFixed(2)}`} tooltip={'体积光柱不透明度。\n使用案例：灯塔/天窗光柱 0.4~0.8。'}>
                    <Slider min={0} max={1} step={0.05} value={Number(v.ray?.intensity ?? 0.6)} onChange={(val) => updateVolume(v.id, { ray: { ...(v.ray || { color: '#a0d8ff', intensity: 0.6, noiseSpeed: 0.3, noiseScale: 1 }), intensity: val } })} />
                  </Field>
                  <Field label="光柱颜色" tooltip={'光柱颜色（additive 叠加）。\n使用案例：暖色 #ffd9a0 用于夕阳。'}>
                    <ColorPicker size="small" value={v.ray?.color ?? '#a0d8ff'} onChange={(c) => updateVolume(v.id, { ray: { ...(v.ray || { color: '#a0d8ff', intensity: 0.6, noiseSpeed: 0.3, noiseScale: 1 }), color: c.toHexString() } })} showText />
                  </Field>
                </>
              )}
              {v.kind === 'fogBox' && (
                <>
                  <Field label={`雾浓度 ${Number(v.vol?.density ?? 0.35).toFixed(2)}`} tooltip={'雾盒内雾的密度（沿视线累积，越大越浓）。\n使用案例：干冰雾 0.25~0.5；浓雾 0.8+。'}>
                    <Slider data-testid={`vol-fog-density-${i}`} min={0} max={1} step={0.02} value={Number(v.vol?.density ?? 0.35)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), density: val } })} />
                  </Field>
                  <Field label={`不透明度 ${Number(v.vol?.opacity ?? 0.55).toFixed(2)}`} tooltip={'雾盒整体不透明度上限（1=完全遮蔽）。\n使用案例：氛围薄雾 0.4~0.6。'}>
                    <Slider min={0} max={1} step={0.02} value={Number(v.vol?.opacity ?? 0.55)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), opacity: val } })} />
                  </Field>
                  <Field label="雾颜色" tooltip={'雾盒颜色。\n使用案例：冷雾 #cfe6f5；暖雾 #ffe6c8。'}>
                    <ColorPicker size="small" value={v.vol?.color ?? '#cfe6f5'} onChange={(c) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), color: c.toHexString() } })} showText />
                  </Field>
                  <Space size={6} style={{ marginBottom: 4 }}>
                    <Text style={{ fontSize: 11, color: '#999' }}>噪声缩放</Text>
                    <InputNumber size="small" id={`vol-fog-noise-${i}`} style={{ width: 64 }} min={0.2} max={3} step={0.1} value={Number(v.vol?.noiseScale ?? 1)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), noiseScale: Math.max(0.2, Number(val) || 1) } })} />
                    <Text style={{ fontSize: 11, color: '#999' }}>流速</Text>
                    <InputNumber size="small" id={`vol-fog-speed-${i}`} style={{ width: 64 }} min={0} max={1} step={0.05} value={Number(v.vol?.noiseSpeed ?? 0.25)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), noiseSpeed: Math.max(0, Number(val) || 0) } })} />
                    <Text style={{ fontSize: 11, color: '#999' }}>步数</Text>
                    <Select size="small" id={`vol-fog-steps-${i}`} style={{ width: 76 }} value={Number(v.vol?.steps ?? 32)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), steps: Number(val) } })} options={[{ value: 8, label: '8' }, { value: 16, label: '16' }, { value: 24, label: '24' }, { value: 32, label: '32' }, { value: 48, label: '48' }, { value: 64, label: '64' }]} />
                  </Space>
                  <Text style={{ fontSize: 10, color: '#666' }}>每帧沿视线步进采样（默认 32 步）——低性能设备建议 ≤16 或关闭；「渲染 → 性能档」会联动全部雾盒步数</Text>
                </>
              )}
              {v.kind === 'cloudImpostor' && (
                <>
                  <Field label={`云量 ${Number(v.vol?.density ?? 0.5).toFixed(2)}（${cloudCountFromDensity(Number(v.vol?.density ?? 0.5))} 片贴片）`} tooltip={'云量=噪声贴片数量（3~16 片，越多越饱满、越吃填充率）。\n使用案例：薄云 0.3；浓云团 0.8。'}>
                    <Slider data-testid={`vol-cloud-density-${i}`} min={0} max={1} step={0.02} value={Number(v.vol?.density ?? 0.5)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), density: val } })} />
                  </Field>
                  <Field label={`不透明度 ${Number(v.vol?.opacity ?? 0.5).toFixed(2)}`} tooltip={'云团整体不透明度。\n使用案例：轻纱云 0.35；浓云 0.7。'}>
                    <Slider min={0} max={1} step={0.02} value={Number(v.vol?.opacity ?? 0.5)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), opacity: val } })} />
                  </Field>
                  <Field label="云颜色" tooltip={'云团颜色。\n使用案例：白云 #ffffff；晚霞云 #ffd9c0。'}>
                    <ColorPicker size="small" value={v.vol?.color ?? '#ffffff'} onChange={(c) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), color: c.toHexString() } })} showText />
                  </Field>
                  <Space size={6} style={{ marginBottom: 4 }}>
                    <Text style={{ fontSize: 11, color: '#999' }}>噪声缩放</Text>
                    <InputNumber size="small" id={`vol-cloud-noise-${i}`} style={{ width: 64 }} min={0.2} max={3} step={0.1} value={Number(v.vol?.noiseScale ?? 1)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), noiseScale: Math.max(0.2, Number(val) || 1) } })} />
                    <Text style={{ fontSize: 11, color: '#999' }}>流速</Text>
                    <InputNumber size="small" id={`vol-cloud-speed-${i}`} style={{ width: 64 }} min={0} max={1} step={0.05} value={Number(v.vol?.noiseSpeed ?? 0.3)} onChange={(val) => updateVolume(v.id, { vol: { ...(v.vol ?? {}), noiseSpeed: Math.max(0, Number(val) || 0) } })} />
                  </Space>
                  <Text style={{ fontSize: 10, color: '#666' }}>面向相机的噪声贴片叠加（成本≈透明面片）——配合性能档：低档建议减少云量</Text>
                </>
              )}
            </div>
          ))}
        </Form>
      ),
    },
    {
      key: 'annotation',
      label: '标绘',
      children: (
        <Form layout="vertical" size="small">
          <Space size={4} wrap style={{ marginBottom: 6 }}>
            {([['point', '点'], ['text', '文字'], ['polyline', '线'], ['polygon', '面'], ['rect', '矩形'], ['circle', '圆']] as Array<[AnnotationKind, string]>).map(([k, label]) => (
              <Button size="small" key={k} data-testid={`anno-draw-${k}`} type={annotationDrawKind === k ? 'primary' : 'default'} onClick={() => void tryDrawAnnotation(k)}>{label}</Button>
            ))}
          </Space>
          <Space size={4} wrap style={{ marginBottom: 6 }}>
            {([['ellipse', '椭圆'], ['sector', '扇形'], ['freehandLine', '自由线'], ['freehandPolygon', '自由面'], ['curve', '曲线']] as Array<[AnnotationKind, string]>).map(([k, label]) => (
              <Button size="small" key={k} data-testid={`anno-draw-${k}`} type={annotationDrawKind === k ? 'primary' : 'default'} onClick={() => void tryDrawAnnotation(k)}>{label}</Button>
            ))}
          </Space>
          <Space size={4} wrap style={{ marginBottom: 6 }}>
            {([['arrowStraight', '直箭头'], ['arrowFine', '细箭头'], ['arrowCurve', '曲线箭头']] as Array<[AnnotationKind, string]>).map(([k, label]) => (
              <Button size="small" key={k} data-testid={`anno-draw-${k}`} type={annotationDrawKind === k ? 'primary' : 'default'} onClick={() => void tryDrawAnnotation(k)}>{label}</Button>
            ))}
          </Space>
          <Space size={6} wrap style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, color: '#999' }}>落点</Text>
            <Segmented
              size="small"
              data-testid="anno-plane-mode"
              value={annotationPlaneMode}
              onChange={(v) => setAnnotationPlaneMode(v as 'ground' | 'model')}
              options={[{ label: '地面', value: 'ground' }, { label: '模型表面', value: 'model' }]}
            />
            <Text style={{ fontSize: 11, color: '#999' }}>吸附</Text>
            <Switch size="small" data-testid="anno-snap" checked={annotationSnap} onChange={setAnnotationSnap} />
          </Space>
          {annotationDrawKind && (
            <Space size={6} style={{ marginBottom: 6 }}>
              <Text style={{ fontSize: 11, color: '#ffd666' }}>绘制中：{drawHint(annotationDrawKind)}</Text>
              {needsExplicitFinish(annotationDrawKind) && (
                <Button size="small" type="primary" data-testid="anno-finish" onClick={() => window.dispatchEvent(new CustomEvent('dtwince:annotation-finish'))}>完成</Button>
              )}
              <Button size="small" data-testid="anno-cancel" onClick={() => setAnnotationDrawKind(null)}>取消</Button>
            </Space>
          )}
          <Space size={6} wrap style={{ marginBottom: 6 }}>
            <Button size="small" data-testid="anno-export-geojson" onClick={exportGeoJson}>导出 GeoJSON</Button>
            <Button size="small" data-testid="anno-import-geojson" onClick={() => geojsonInputRef.current?.click()}>导入 GeoJSON</Button>
            <input
              ref={geojsonInputRef}
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              data-testid="anno-geojson-file"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) void importGeoJsonFile(f); e.target.value = ''; }}
            />
          </Space>
          {/* M3：标绘模板（市场下发；免费/付费/积分） */}
          <div data-testid="anno-template-section" style={{ marginBottom: 6 }}>
            <Text style={{ fontSize: 11, color: '#999' }}>标绘模板（市场）</Text>
            {annoTemplates.length === 0 ? (
              <Text style={{ fontSize: 11, color: '#666', display: 'block' }}>暂无可用模板（推广后台「组件管理」上传并上架后在此应用）</Text>
            ) : annoTemplates.map((t, ti) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <Text style={{ fontSize: 12, color: '#ddd', flex: 1 }} ellipsis>{t.name}</Text>
                <Tag color={presetPriceLabel(t).color} style={{ marginInlineEnd: 0 }}>{presetPriceLabel(t).text}</Tag>
                <Button size="small" data-testid={`anno-template-apply-${ti}`} onClick={() => void applyMarketPreset(t, 'annotation')}>应用</Button>
              </div>
            ))}
          </div>
          {annotations.length === 0 && <Text style={{ fontSize: 11, color: '#888' }}>暂无标绘（选择上方图元后在场景中绘制；Esc 取消）</Text>}
          {annotations.map((a, i) => (
            <div key={a.id} data-testid={`anno-card-${i}`} style={{ border: annotationSelectedId === a.id ? '1px solid #ffd666' : '1px solid #30475e', borderRadius: 4, padding: 6, marginBottom: 6 }}>
              <Space size={6} style={{ marginBottom: 4 }}>
                <Tag color={isArrowKind(a.kind) ? 'gold' : 'blue'} style={{ marginInlineEnd: 0 }}>{kindLabel(a.kind)}</Tag>
                <Switch size="small" data-testid={`anno-visible-${i}`} checked={a.visible} onChange={(val) => updateAnnotation(a.id, { visible: val })} />
                <Button size="small" type="text" data-testid={`anno-select-${i}`} icon={<AimOutlined />} onClick={() => useSelectionStore.getState().selectAnnotation(a.id)} />
                <Button size="small" type="text" data-testid={`anno-reground-${i}`} onClick={() => regroundAnnotation(a)}>贴地</Button>
                <Button size="small" type="text" danger data-testid={`anno-del-${i}`} icon={<DeleteOutlined />} onClick={() => removeAnnotation(a.id)} />
              </Space>
              {a.kind === 'text' ? (
                <Space size={6} style={{ marginBottom: 4 }}>
                  <Input size="small" data-testid={`anno-text-content-${i}`} style={{ width: 150 }} value={a.style.text?.content ?? ''} onChange={(e) => updateAnnotation(a.id, { style: { ...a.style, text: { ...(a.style.text ?? { content: '文字标注', fontSize: 16, color: '#ffffff' }), content: e.target.value } } })} />
                  <InputNumber size="small" style={{ width: 70 }} min={8} max={72} value={a.style.text?.fontSize ?? 16} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, text: { ...(a.style.text ?? { content: '文字标注', fontSize: 16, color: '#ffffff' }), fontSize: Number(val) || 16 } } })} />
                </Space>
              ) : (
                <Input size="small" style={{ width: 150, marginBottom: 4 }} value={a.name} onChange={(e) => updateAnnotation(a.id, { name: e.target.value })} />
              )}
              <Space size={6} style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>线色</Text>
                <ColorPicker size="small" value={a.style.lineColor} onChange={(c) => updateAnnotation(a.id, { style: { ...a.style, lineColor: c.toHexString() } })} />
                <Text style={{ fontSize: 11, color: '#999' }}>线宽</Text>
                <InputNumber size="small" id={`anno-width-${i}`} style={{ width: 64 }} min={1} max={20} value={a.style.lineWidth} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, lineWidth: Math.max(1, Number(val) || 1) } })} />
                <Text style={{ fontSize: 11, color: '#999' }}>虚线</Text>
                <Switch size="small" checked={a.style.dashed} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, dashed: val } })} />
              </Space>
              {isArrowKind(a.kind) && (
                <Space size={6} style={{ marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, color: '#999' }}>头大小</Text>
                  <InputNumber size="small" id={`anno-head-${i}`} style={{ width: 60 }} min={0.2} max={3} step={0.1} value={a.style.arrow?.headSize ?? 1} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, arrow: { ...(a.style.arrow || {}), headSize: Number(val) || 1 } } })} />
                  <Text style={{ fontSize: 11, color: '#999' }}>干宽</Text>
                  <InputNumber size="small" id={`anno-tail-${i}`} style={{ width: 60 }} min={0.2} max={3} step={0.1} value={a.style.arrow?.tailWidth ?? 1} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, arrow: { ...(a.style.arrow || {}), tailWidth: Number(val) || 1 } } })} />
                </Space>
              )}
              {isAreaKind(a.kind) && (
                <Field label={`填充透明度 ${(a.style.fillOpacity ?? 0.18).toFixed(2)}`} tooltip={'面类图元（面/矩形/圆/椭圆/扇形/自由面/箭头）的填充不透明度。\n使用案例：区域强调 0.15~0.35；箭头实心 0.55。'}>
                  <Slider min={0} max={1} step={0.02} value={a.style.fillOpacity ?? 0.18} onChange={(val) => updateAnnotation(a.id, { style: { ...a.style, fillOpacity: val } })} />
                </Field>
              )}
              <Space size={6} style={{ marginBottom: 4 }}>
                <Text style={{ fontSize: 11, color: '#999' }}>生长</Text>
                <Switch size="small" data-testid={`anno-grow-${i}`} checked={!!a.animation?.grow} onChange={(val) => updateAnnotation(a.id, { animation: { ...(a.animation || {}), grow: val } })} />
                <Text style={{ fontSize: 11, color: '#999' }}>淡入</Text>
                <Switch size="small" data-testid={`anno-fade-${i}`} checked={!!a.animation?.fade} onChange={(val) => updateAnnotation(a.id, { animation: { ...(a.animation || {}), fade: val } })} />
              </Space>
              <div style={{ borderTop: '1px dashed #2a3b4d', paddingTop: 4 }}>
                <Space size={6} wrap style={{ marginBottom: 4 }}>
                  <Text style={{ fontSize: 11, color: '#999' }}>数据绑定</Text>
                  <Select
                    size="small" style={{ width: 120 }} data-testid={`anno-bind-device-${i}`} allowClear showSearch placeholder="设备"
                    value={a.binding?.deviceId || undefined}
                    options={iotDevices}
                    filterOption={(input, option) => String(option?.label ?? '').toLowerCase().includes(input.toLowerCase()) || String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())}
                    onChange={(v) => updateAnnotation(a.id, { binding: { ...(a.binding || {}), deviceId: v || undefined, pointKey: undefined } })}
                  />
                  <Select
                    size="small" style={{ width: 130 }} data-testid={`anno-bind-point-${i}`} allowClear showSearch placeholder="点位 key"
                    value={a.binding?.pointKey || undefined}
                    options={Object.keys(deviceMap[a.binding?.deviceId || '']?.points ?? {}).sort().map((k) => ({ value: k, label: k }))}
                    onChange={(v) => updateAnnotation(a.id, { binding: { ...(a.binding || {}), pointKey: v || undefined } })}
                  />
                </Space>
                {(a.binding?.rules ?? []).map((r, ri) => (
                  <div key={ri} style={{ display: 'flex', gap: 4, marginBottom: 4, alignItems: 'center' }}>
                    <InputNumber size="small" style={{ width: 64 }} placeholder="min" value={r.min} onChange={(val) => { const rules = [...(a.binding?.rules || [])]; rules[ri] = { ...rules[ri], min: Number(val) || 0 }; updateAnnotation(a.id, { binding: { ...(a.binding || {}), rules } }); }} />
                    <Text style={{ color: '#555', fontSize: 11 }}>~</Text>
                    <InputNumber size="small" style={{ width: 64 }} placeholder="max" value={r.max} onChange={(val) => { const rules = [...(a.binding?.rules || [])]; rules[ri] = { ...rules[ri], max: Number(val) || 0 }; updateAnnotation(a.id, { binding: { ...(a.binding || {}), rules } }); }} />
                    <ColorPicker size="small" value={r.color} onChange={(c) => { const rules = [...(a.binding?.rules || [])]; rules[ri] = { ...rules[ri], color: c.toHexString() }; updateAnnotation(a.id, { binding: { ...(a.binding || {}), rules } }); }} />
                    <Button size="small" type="text" danger onClick={() => { const rules = (a.binding?.rules || []).filter((_, j) => j !== ri); updateAnnotation(a.id, { binding: { ...(a.binding || {}), rules } }); }}>×</Button>
                  </div>
                ))}
                <Button
                  size="small" type="dashed" data-testid={`anno-bind-rule-add-${i}`}
                  onClick={() => { const rules = [...(a.binding?.rules || []), { min: 0, max: 100, color: '#ff4d4f' }]; updateAnnotation(a.id, { binding: { ...(a.binding || {}), rules } }); }}
                >
                  + 阈值颜色
                </Button>
                {a.kind === 'text' && <Text style={{ fontSize: 10, color: '#666', marginLeft: 6 }}>文字内容含 {'{value}'} 时替换为点位值</Text>}
              </div>
            </div>
          ))}
        </Form>
      ),
    },
  ], [
    boundedMode, showGround, showGrid, gridAuto, gridCellSize, cameraStrategy, environment, sky,
    shadow, uploading, progress, loadColor, loadText, fps, frameMs, handleUpload, perfTier,
    resetBasic, resetSky, resetLight, resetShadow, resetFog, postprocessing, volumes, volumeSelectedId, addVolume, updateVolume, removeVolume, selectVolume,
    annotations, updateAnnotation, removeAnnotation, annotationSelectedId, annotationDrawKind, setAnnotationDrawKind,
    annotationPlaneMode, annotationSnap, iotDevices, deviceMap, currentScene, tryDrawAnnotation, tryAddVolume,
    regroundAnnotation, exportGeoJson, importGeoJsonFile, drawHint,
    marketComponents, annoTemplates, volPresets, applyMarketPreset,
  ]);

  return (
    <div style={{ padding: '8px 8px 16px' }} data-testid="scene-settings-panel">
      <Text strong style={{ fontSize: 13 }}>场景属性</Text>
      <Text style={{ fontSize: 11, color: '#888', marginLeft: 8 }}>（选中场景对象时显示）</Text>
      <Collapse
        size="small"
        ghost
        defaultActiveKey={['sky', 'light']}
        items={items}
        style={{ marginTop: 6 }}
      />
    </div>
  );
}
